/** HTTP client for the Edge Functions. Errors become ApiError with a stable code. */
import { ApiError, ERROR_CODES, ERROR_TEXT, type ErrorCode } from '@shared/errors.ts';
import type {
  Agenda,
  AgendaBooking,
  AssistantRes,
  BookingView,
  CreateBookingReq,
  CreateBookingRes,
  OwnerSettings,
  OwnerTenantRef,
  PublicTenant,
  Slot,
  Stats,
  TelegramStatus,
} from '@shared/contracts.ts';
import { FUNCTIONS_URL, SUPABASE_ANON_KEY } from './env.ts';

export { ApiError };

export function errorText(e: unknown): string {
  if (e instanceof ApiError) return ERROR_TEXT[e.code];
  if (e instanceof TypeError) return 'Нет связи с сервером. Проверьте интернет и попробуйте ещё раз.';
  return ERROR_TEXT.internal;
}

interface RequestOpts {
  method?: string;
  body?: unknown;
  form?: FormData;
  token?: string | null;
  bookingToken?: string | null;
  signal?: AbortSignal;
}

async function request<T>(fn: string, path: string, opts: RequestOpts = {}): Promise<T> {
  const headers: Record<string, string> = { apikey: SUPABASE_ANON_KEY };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (opts.token) headers.authorization = `Bearer ${opts.token}`;
  if (opts.bookingToken) headers['x-booking-token'] = opts.bookingToken;
  const res = await fetch(`${FUNCTIONS_URL}/${fn}${path}`, {
    method: opts.method ?? (opts.body !== undefined || opts.form ? 'POST' : 'GET'),
    headers,
    body: opts.form ?? (opts.body !== undefined ? JSON.stringify(opts.body) : undefined),
    signal: opts.signal,
    // Never let the HTTP cache keep personal data.
    cache: 'no-store',
  });
  const text = await res.text();
  const json = text ? safeParse(text) : null;
  // A non-JSON 2xx means we are not talking to the API (e.g. missing VITE_SUPABASE_URL).
  if (res.ok && text && json === null) throw new ApiError('internal');
  if (!res.ok) {
    const code = (json as { error?: { code?: string } } | null)?.error?.code;
    throw new ApiError((ERROR_CODES as readonly string[]).includes(code ?? '') ? (code as ErrorCode) : res.status === 429 ? 'rate_limited' : 'internal');
  }
  return json as T;
}

function safeParse(t: string): unknown {
  try {
    return JSON.parse(t);
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------- public
export const publicApi = {
  tenant: (slug: string, signal?: AbortSignal) => request<PublicTenant>('public-api', `/t/${slug}`, { signal }),
  slots: (slug: string, serviceId: string, from: string, days: number, signal?: AbortSignal) =>
    request<{ slots: Slot[] }>('public-api', `/t/${slug}/slots?service=${serviceId}&from=${from}&days=${days}`, { signal }).then((r) => r.slots),
  createBooking: (slug: string, body: CreateBookingReq) => request<CreateBookingRes>('public-api', `/t/${slug}/bookings`, { body }),
  booking: (slug: string, token: string, signal?: AbortSignal) => request<BookingView>('public-api', `/t/${slug}/booking`, { bookingToken: token, signal }),
  cancel: (slug: string, token: string, opKey: string) =>
    request<{ status: string }>('public-api', `/t/${slug}/booking/cancel`, { bookingToken: token, body: { opKey } }),
  reschedule: (slug: string, token: string, startsAt: string, opKey: string) =>
    request<{ starts_at: string }>('public-api', `/t/${slug}/booking/reschedule`, { bookingToken: token, body: { startsAt, opKey } }),
  subscribePush: (slug: string, token: string, sub: PushSubscriptionJSON) =>
    request<{ subscribed: boolean; reminder_at: string | null }>('public-api', `/t/${slug}/booking/push`, { bookingToken: token, body: sub }),
  unsubscribePush: (slug: string, token: string, endpoint: string) =>
    request('public-api', `/t/${slug}/booking/push/remove`, { bookingToken: token, body: { endpoint } }),
  vapidKey: () => request<{ key: string | null }>('public-api', '/push/vapid-public-key').then((r) => r.key),
};

// ---------------------------------------------------------------- assistant
export const assistantApi = {
  client: (slug: string, messages: { role: 'user' | 'assistant'; content: string }[], bookingToken: string | null) =>
    request<AssistantRes>('assistant', `/t/${slug}/client`, { body: { messages }, bookingToken }),
  owner: (slug: string, token: string, messages: { role: 'user' | 'assistant'; content: string }[]) =>
    request<AssistantRes>('assistant', `/t/${slug}/owner`, { body: { messages }, token }),
};

// ---------------------------------------------------------------- owner
export function ownerApi(token: string) {
  const o = (path: string, opts: RequestOpts = {}) => request<unknown>('owner-api', path, { ...opts, token });
  return {
    me: () => o('/me') as Promise<{ tenants: OwnerTenantRef[] }>,
    agenda: (t: string, from: string, to: string) => o(`/t/${t}/agenda?from=${from}&to=${to}`) as Promise<Agenda>,
    booking: (t: string, id: string) => o(`/t/${t}/bookings/${id}`) as Promise<AgendaBooking>,
    stats: (t: string, q: { preset: string } | { from: string; to: string }) =>
      o(`/t/${t}/stats?${new URLSearchParams(q as Record<string, string>)}`) as Promise<Stats>,
    slots: (t: string, serviceId: string, from: string, days = 1) =>
      o(`/t/${t}/slots?service=${serviceId}&from=${from}&days=${days}`) as Promise<{ slots: (Slot & { free_resources: number })[] }>,
    createBooking: (t: string, body: unknown) => o(`/t/${t}/bookings`, { body }) as Promise<{ booking_id: string }>,
    reschedule: (t: string, id: string, body: unknown) => o(`/t/${t}/bookings/${id}/reschedule`, { body }),
    cancel: (t: string, id: string, body: unknown) => o(`/t/${t}/bookings/${id}/cancel`, { body }),
    status: (t: string, id: string, body: unknown) => o(`/t/${t}/bookings/${id}/status`, { body }),
    payments: (t: string, id: string) =>
      o(`/t/${t}/bookings/${id}/payments`) as Promise<{ payments: { id: string; kind: 'payment' | 'refund'; amount_cents: number; method: string; paid_at: string; note: string }[] }>,
    pay: (t: string, id: string, body: unknown) => o(`/t/${t}/bookings/${id}/payments`, { body }),
    block: (t: string, body: unknown) => o(`/t/${t}/blocks`, { body }),
    releaseBlock: (t: string, id: string) => o(`/t/${t}/blocks/${id}`, { method: 'DELETE' }),
    settings: (t: string) => o(`/t/${t}/settings`) as Promise<OwnerSettings>,
    saveProfile: (t: string, body: unknown) => o(`/t/${t}/settings/profile`, { method: 'PUT', body }),
    saveService: (t: string, body: unknown) => o(`/t/${t}/services`, { method: 'PUT', body }) as Promise<{ id: string }>,
    saveResource: (t: string, body: unknown) => o(`/t/${t}/resources`, { method: 'PUT', body }) as Promise<{ id: string }>,
    saveHours: (t: string, body: unknown) => o(`/t/${t}/hours`, { method: 'PUT', body }),
    uploadMedia: (t: string, form: FormData) => o(`/t/${t}/media`, { form, method: 'POST' }) as Promise<{ id: string; path: string }>,
    patchMedia: (t: string, id: string, body: unknown) => o(`/t/${t}/media/${id}`, { method: 'PATCH', body }),
    deleteMedia: (t: string, id: string) => o(`/t/${t}/media/${id}`, { method: 'DELETE' }),
    subscribePush: (t: string, sub: PushSubscriptionJSON) => o(`/t/${t}/push`, { body: sub }),
    unsubscribePush: (t: string, endpoint: string) => o(`/t/${t}/push/remove`, { body: { endpoint } }),
    telegram: (t: string) => o(`/t/${t}/telegram`) as Promise<TelegramStatus>,
    telegramLink: (t: string) => o(`/t/${t}/telegram/link`, { body: {} }) as Promise<{ url: string; expires_at: string }>,
    telegramUnlink: (t: string, chat: string) => o(`/t/${t}/telegram/remove`, { body: { chat } }),
  };
}
export type OwnerApi = ReturnType<typeof ownerApi>;
